// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useMemo } from 'react';
import IconFilterReset from 'components/treactui-template/icons/IconFilterReset';
import { useTranslation } from 'react-i18next';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import { useParams } from 'react-router-dom';
import ParamsWithId from 'model/ParamsWithId';
import { dataFilters, DataFilter, DataFilterType } from './filters-defaults';
import { UseCase } from '@tls/sw91-communication/types/com.base';
import { Button, FilterContent } from '@tls/treact-ui';
import { IconClose } from '@tls/treact-ui/icons';
interface Props {
  filter: DataFilter;
  setFilter: (filter: DataFilter) => void;
  setShowFilter: (showFilter: boolean) => void;
  projectTags: string[];
}

export default function DataFilters({ filter, setFilter, setShowFilter, projectTags }: Readonly<Props>) {
  const { t } = useTranslation();
  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);
  const modelClassesWithoutBackground = useMemo(
    () => selectedProject?.projectUseCase?.modelClasses?.filter(modelClass => modelClass.classIndex !== 0) ?? [],
    [selectedProject?.projectUseCase?.modelClasses]
  );

  const filters = useMemo(() => {
    let _dataFilters = dataFilters;

    if (selectedProject?.projectUseCase?.useCase === UseCase.COMPONENT_DETECTION) {
      _dataFilters = dataFilters.filter(f => f.id !== DataFilterType.Dataset && f.id !== DataFilterType.ProcessTag);
    }

    return _dataFilters.map(f => {
      if (f.id === 'label') {
        return {
          id: f.id,
          label: t(f.label),
          options: [
            ...f.options.map(option => ({ id: option.id, label: t(option.label) })),
            ...(modelClassesWithoutBackground.map(modelClass => ({
              id: '' + modelClass.classIndex,
              label: modelClass.className,
            })) ?? []),
          ],
        };
      }
      if (f.id === 'imageTag') {
        return {
          id: f.id,
          label: t(f.label),
          options: [
            ...f.options.map(option => ({ id: option.id, label: t(option.label) })),
            ...(projectTags.map(tag => ({ id: tag, label: tag })) ?? []),
          ],
        };
      }
      return {
        id: f.id,
        label: t(f.label),
        options: f.options.map(option => ({ id: option.id, label: t(option.label) })),
      };
    });
  }, [modelClassesWithoutBackground, selectedProject?.projectUseCase?.useCase, projectTags, t]);

  const resetFilter = useCallback(() => {
    setFilter({
      dataset: [],
      processTag: [],
      label: [],
      imageTag: [],
    });
  }, [setFilter]);

  const updateFilter = useCallback(
    (filterType: string, selectedOptions: { id: string }[]) => {
      setFilter({
        ...filter,
        [filterType]: selectedOptions.map(option => option.id),
      });
    },
    [filter, setFilter]
  );

  const selectLabel = (option: any) => option.label;

  const selectKey = (option: any) => option.id;

  return (
    <>
      {filters.map(f => (
        <FilterContent
          key={`Filter-${f.id}`}
          label={f.label}
          keySelector={selectKey}
          labelSelector={selectLabel}
          options={f.options}
          selectedOptions={f.options.filter(option => filter[f.id]?.includes(option.id)) ?? []}
          onChange={selectedOptions => updateFilter(f.id, selectedOptions)}
          noResultText={t('noResults')}
          closeLabel={t('close')}
          saveLabel={t('filter.save')}
          selectedItemsLabelSelector={selectedItems => {
            if (selectedItems.length === 1) {
              return selectedItems[0].label ?? '';
            }

            let labelKey: string;
            if (selectedItems.length === 0) {
              labelKey = 'filter.none';
            } else if (selectedItems.length === f.options.length) {
              labelKey = 'filter.everythingLabel';
            } else {
              labelKey = 'filter.multiple';
            }

            return t(labelKey, { count: selectedItems.length });
          }}
          data-testid={`data-filter-${f.id}-dropdown`}
          dataTestIdSelector={option => `data-filter-${f.id}-option-${option.id}`}
        />
      ))}
      <Button
        icon={<IconFilterReset width='1.5rem' key='filter-reset-button' />}
        variant='secondary'
        label={t('filter.reset')}
        onClick={resetFilter}
        data-testid='data-filter-reset-button'
      />
      <Button
        icon={<IconClose key='filter-close-button' />}
        variant='secondary'
        onClick={() => setShowFilter(false)}
        data-testid='data-filter-close-button'
      />
    </>
  );
}
